import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    ArrowLeft, User, Mail, ShieldAlert, Trash2, Loader2, AlertTriangle,
    AtSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { UsernameEditor } from '../components/UsernameEditor';

const API = '/api';

/**
 * SettingsPage - A consolidated settings hub. Today the centerpiece
 * is the **Account Deletion** flow, which Google Play / Apple now
 * require for any app that creates accounts. Future settings (theme,
 * notification prefs, default category, etc.) slot in as additional
 * <SettingsSection> blocks above the danger zone.
 *
 * Design notes:
 * - Single column on mobile, max 720px wide on desktop so it never
 *   stretches into a hard-to-scan slab.
 * - The danger zone is visually quarantined (red border, two-step
 *   reveal) so an accidental tap on a category list can't initiate
 *   account deletion.
 */
export default function SettingsPage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    if (!user) {
        // Not logged in - bounce to landing. Loading states are handled
        // upstream by ProtectedRoute (if used) - this is a defensive
        // fallback so a direct deep-link doesn't crash.
        navigate('/', { replace: true });
        return null;
    }

    return (
        <div className="min-h-screen bg-background" data-testid="settings-page">
            <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-border">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(-1)}
                        className="h-9 w-9 p-0"
                        data-testid="settings-back-btn"
                        aria-label="Back"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div>
                        <h1 className="font-heading font-bold text-lg leading-tight">Settings</h1>
                        <p className="text-xs text-muted-foreground">Account preferences &amp; data</p>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
                {/* Account Info - read-only identity fields. */}
                <SettingsSection
                    icon={User}
                    title="Account"
                    description="Your sign-in identity"
                >
                    <Row label="Email" icon={Mail}>
                        <span className="text-sm font-medium text-zinc-900 break-all">{user.email || '-'}</span>
                    </Row>
                    <Row label="Name" icon={User}>
                        <span className="text-sm font-medium text-zinc-900">{user.name || '-'}</span>
                    </Row>
                </SettingsSection>

                {/* Public profile username - inline editor matching the
                    one on the "Public profile" overlay. Full live
                    availability check + save. */}
                <SettingsSection
                    icon={AtSign}
                    title="Public profile"
                    description="The URL where people find your public bookmarks"
                >
                    <div className="px-4 py-4">
                        <UsernameEditor testIdPrefix="settings-username" />
                    </div>
                </SettingsSection>

                {/* Danger zone */}
                <DangerZone user={user} onAccountDeleted={() => {
                    // Logout call clears the client-side auth context;
                    // the backend has already revoked the session.
                    logout?.();
                    navigate('/', { replace: true });
                }} />
            </main>
        </div>
    );
}


/* ── Section primitives ─────────────────────────────────────────── */

const SettingsSection = ({ icon: Icon, title, description, children }) => (
    <section className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-start gap-3">
            {Icon && (
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-primary" />
                </div>
            )}
            <div>
                <h2 className="font-semibold text-sm text-zinc-900 leading-tight">{title}</h2>
                {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
            </div>
        </div>
        <div className="divide-y divide-border">{children}</div>
    </section>
);

const Row = ({ label, icon: Icon, children }) => (
    <div className="px-4 py-3 flex items-center justify-between gap-4">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground shrink-0">
            {Icon && <Icon className="w-3 h-3" />}
            {label}
        </span>
        <div className="text-right min-w-0">{children}</div>
    </div>
);


/* ── Danger zone (account deletion) ─────────────────────────────── */

const DangerZone = ({ user, onAccountDeleted }) => {
    // Two-step reveal: the destructive UI is hidden behind a click so
    // a tap on the section header can't accidentally start it. Once
    // expanded, we further gate the actual delete behind typing the
    // user's email - same guardrail the backend enforces.
    const [expanded, setExpanded] = useState(false);
    const [confirmEmail, setConfirmEmail] = useState('');
    const [deleting, setDeleting] = useState(false);
    const emailMatches = confirmEmail.trim().toLowerCase() === (user.email || '').trim().toLowerCase();

    const handleDelete = async () => {
        if (!emailMatches || deleting) return;
        // Final native confirm - belt and braces for accidental
        // double-taps on touchscreens.
        if (!window.confirm('Last chance: this permanently deletes your account, all bookmarks, notes, AI chats, and subscriptions. Continue?')) {
            return;
        }
        setDeleting(true);
        try {
            const res = await fetch(`${API}/auth/me`, {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirmation_email: confirmEmail.trim() }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d.detail || `HTTP ${res.status}`);
            }
            toast.success('Account deleted. You will be signed out.');
            // Small delay so the toast is visible before we redirect.
            setTimeout(onAccountDeleted, 800);
        } catch (e) {
            toast.error(e.message || 'Could not delete account. Please try again.');
            setDeleting(false);
        }
    };

    return (
        <section className="bg-white rounded-2xl border-2 border-red-200 overflow-hidden" data-testid="danger-zone">
            <div className="px-4 py-3 border-b border-red-200 bg-red-50/50 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                    <ShieldAlert className="w-4 h-4 text-red-600" />
                </div>
                <div>
                    <h2 className="font-semibold text-sm text-red-900 leading-tight">Danger Zone</h2>
                    <p className="text-xs text-red-700/80 mt-0.5">Permanent actions you can&apos;t undo</p>
                </div>
            </div>

            <div className="p-4 space-y-3">
                <div className="text-sm text-zinc-700 space-y-2">
                    <p>
                        Deleting your account is permanent. We will remove:
                    </p>
                    <ul className="text-xs text-zinc-600 space-y-1 pl-4 list-disc">
                        <li>Your profile, email, and login credentials</li>
                        <li>All saved bookmarks, notes, collections, and AI chat history</li>
                        <li>Reminders, notifications, and personal recommendations</li>
                        <li>Your active subscription (if any) - cancel via Stripe to avoid future charges</li>
                    </ul>
                    <p className="text-xs text-zinc-600">
                        Payment records are anonymised (not deleted) so tax / refund audits still work. We respond within 30 days as required by GDPR / CCPA.
                    </p>
                </div>

                {!expanded ? (
                    <Button
                        variant="outline"
                        onClick={() => setExpanded(true)}
                        className="w-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                        data-testid="delete-account-reveal-btn"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete my account
                    </Button>
                ) : (
                    <div className="space-y-3 pt-2 border-t border-red-100" data-testid="delete-account-form">
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                            <p className="text-xs text-red-800">
                                To confirm, type your email below: <span className="font-mono font-semibold break-all">{user.email}</span>
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="confirm-email" className="text-xs text-zinc-600">
                                Type your email to confirm
                            </Label>
                            <Input
                                id="confirm-email"
                                type="email"
                                autoComplete="off"
                                value={confirmEmail}
                                onChange={(e) => setConfirmEmail(e.target.value)}
                                placeholder={user.email}
                                className="mt-1 font-mono text-sm"
                                disabled={deleting}
                                data-testid="delete-confirm-email-input"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setExpanded(false);
                                    setConfirmEmail('');
                                }}
                                disabled={deleting}
                                className="flex-1"
                                data-testid="delete-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleDelete}
                                disabled={!emailMatches || deleting}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:bg-red-200"
                                data-testid="delete-confirm-btn"
                            >
                                {deleting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Deleting…
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Permanently delete
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};
